(() => {
  const ROOT_SELECTOR = '[data-sw-install-guide-navigation]';

  function notifyContextChange(root) {
    root.dispatchEvent(new CustomEvent('sw-install:context-change', { bubbles: true }));
  }

  function setupArrowKeys(tablist, tabs) {
    tablist.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

      event.preventDefault();
      const currentIndex = Math.max(0, tabs.indexOf(document.activeElement));
      let nextIndex = currentIndex;

      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
      if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = tabs.length - 1;

      tabs[nextIndex].click();
      tabs[nextIndex].focus();
    });
  }

  function setupNavigation(root) {
    if (root.dataset.swInstallNavigationReady === 'true') return;

    const categoryTabs = [...root.querySelectorAll('[data-sw-install-category-tab]')];
    const categoryPanels = [...root.querySelectorAll('[data-sw-install-category-panel]')];
    const deviceTabs = [...root.querySelectorAll('[data-sw-install-device-tab]')];
    const devicePanels = [...root.querySelectorAll('[data-sw-install-device-panel]')];

    if (!categoryTabs.length || !deviceTabs.length || !devicePanels.length) return;

    root.dataset.swInstallNavigationReady = 'true';
    const selectedDevices = new Map();

    root.querySelectorAll('[data-sw-install-method-tabs]').forEach((methodTabs) => {
      if (methodTabs.dataset.swInstallMethodTabsReady === 'true') return;

      const tabs = [...methodTabs.querySelectorAll('[data-sw-install-method-tab]')];
      const panels = [...methodTabs.querySelectorAll('[data-sw-install-method-panel]')];
      const devicePanel = methodTabs.closest('[data-sw-install-device-panel]');
      const videos = devicePanel
        ? [...devicePanel.querySelectorAll('[data-sw-install-method-video]')]
        : [];
      if (!tabs.length || !panels.length) return;

      methodTabs.dataset.swInstallMethodTabsReady = 'true';

      const showMethod = (method) => {
        const activeTab = tabs.find((tab) => tab.dataset.swInstallMethodTab === method) || tabs[0];
        const activeMethod = activeTab.dataset.swInstallMethodTab;
        const previousMethod = methodTabs.dataset.swInstallActiveMethod;

        if (previousMethod && previousMethod !== activeMethod) {
          notifyContextChange(root);
        }

        tabs.forEach((tab) => {
          const active = tab === activeTab;
          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-selected', String(active));
          tab.tabIndex = active ? 0 : -1;
        });

        panels.forEach((panel) => {
          panel.hidden = panel.dataset.swInstallMethodPanel !== activeMethod;
        });

        videos.forEach((video) => {
          video.hidden = video.dataset.swInstallMethodVideo !== activeMethod;
        });

        methodTabs.dataset.swInstallActiveMethod = activeMethod;
      };

      tabs.forEach((tab) => {
        tab.addEventListener('click', () => showMethod(tab.dataset.swInstallMethodTab));
      });

      const tablist = methodTabs.querySelector('[role="tablist"]');
      if (tablist) setupArrowKeys(tablist, tabs);
      showMethod('primary');
    });

    function showSelection(category, preferredDevice, updateHash = false) {
      const categoryTab = categoryTabs.find((tab) => tab.dataset.swInstallCategoryTab === category) || categoryTabs[0];
      const activeCategory = categoryTab.dataset.swInstallCategoryTab;
      const tabsInCategory = deviceTabs.filter((tab) => tab.dataset.swInstallDeviceCategory === activeCategory);
      const preferredTab = tabsInCategory.find((tab) => tab.dataset.swInstallDeviceTab === preferredDevice);
      const rememberedTab = tabsInCategory.find((tab) => tab.dataset.swInstallDeviceTab === selectedDevices.get(activeCategory));
      const activeDeviceTab = preferredTab || rememberedTab || tabsInCategory[0];
      const activeDevice = activeDeviceTab?.dataset.swInstallDeviceTab;
      const previousCategory = root.dataset.swInstallActiveCategory;
      const previousDevice = root.dataset.swInstallActiveDevice;

      if (
        previousDevice &&
        (previousCategory !== activeCategory || previousDevice !== activeDevice)
      ) {
        notifyContextChange(root);
      }

      categoryTabs.forEach((tab) => {
        const active = tab === categoryTab;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });

      categoryPanels.forEach((panel) => {
        panel.hidden = panel.dataset.swInstallCategoryPanel !== activeCategory;
      });

      deviceTabs.forEach((tab) => {
        const active = tab === activeDeviceTab;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });

      devicePanels.forEach((panel) => {
        panel.hidden = panel.dataset.swInstallDevicePanel !== activeDevice;
      });

      if (activeDevice) {
        selectedDevices.set(activeCategory, activeDevice);
        root.dataset.swInstallActiveCategory = activeCategory;
        root.dataset.swInstallActiveDevice = activeDevice;

        if (updateHash && window.history?.replaceState) {
          window.history.replaceState(null, '', `#install-${activeDevice}`);
        }
      }
    }

    categoryTabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        showSelection(tab.dataset.swInstallCategoryTab, null, true);
      });
    });

    deviceTabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        showSelection(
          tab.dataset.swInstallDeviceCategory,
          tab.dataset.swInstallDeviceTab,
          true
        );
      });
    });

    const categoryTablist = categoryTabs[0].closest('[role="tablist"]');
    if (categoryTablist) setupArrowKeys(categoryTablist, categoryTabs);

    root.querySelectorAll('.sw-install-guide__device-tabs').forEach((tablist) => {
      const tabs = [...tablist.querySelectorAll('[data-sw-install-device-tab]')];
      if (tabs.length) setupArrowKeys(tablist, tabs);
    });

    const hashPanel = devicePanels.find((panel) => `#${panel.id}` === window.location.hash);
    if (hashPanel) {
      showSelection(
        hashPanel.dataset.swInstallDeviceCategory,
        hashPanel.dataset.swInstallDevicePanel
      );
    } else {
      showSelection(categoryTabs[0].dataset.swInstallCategoryTab);
    }

    root.classList.add('is-navigation-ready');

    window.addEventListener('hashchange', () => {
      const panel = devicePanels.find((item) => `#${item.id}` === window.location.hash);
      if (panel) {
        showSelection(
          panel.dataset.swInstallDeviceCategory,
          panel.dataset.swInstallDevicePanel
        );
      }
    });

    document.addEventListener('shopify:block:select', (event) => {
      const panel = event.target.closest?.('[data-sw-install-device-panel]');
      if (panel && root.contains(panel)) {
        showSelection(
          panel.dataset.swInstallDeviceCategory,
          panel.dataset.swInstallDevicePanel
        );
      }
    });
  }

  function setupAll(scope = document) {
    if (scope.matches?.(ROOT_SELECTOR)) setupNavigation(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(setupNavigation);
  }

  setupAll();
  document.addEventListener('shopify:section:load', (event) => setupAll(event.target));
})();
